'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const testimonials = [
{
  id: 1,
  quote:
  "I used to spend every Sunday afternoon at the laundromat. Now I drop a bag outside my door on Friday and have clean clothes Saturday morning. Genuinely life-changing for finals week.",
  name: 'Maya Chen',
  role: 'Junior, Computer Science · UCLA',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16cf70890-1763298771927.png",
  altText: 'Maya Chen, junior computer science student at UCLA smiling',
  rating: 5,
  highlight: 'Saved my entire Sunday'
},
{
  id: 2,
  quote:
  "I was skeptical — $8 a load seemed like a lot. But when I calculated what I was spending on quarters, detergent, and fabric softener, CampusWash is actually cheaper. Plus I get that time back.",
  name: 'DeShawn Williams',
  role: 'Sophomore, Business · Howard University',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ead12976-1772143683602.png",
  altText: 'DeShawn Williams, sophomore business student at Howard University',
  rating: 5,
  highlight: 'Actually saves money'
},
{
  id: 3,
  quote:
  "The tracking is so good. I get a text when they pick up, when it's being washed, and when it's back at my door. My clothes have never been lost once in a whole semester.",
  name: 'Priya Nair',
  role: 'Senior, Pre-Med · Boston University',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17f66c860-1773070762970.png",
  altText: 'Priya Nair, senior pre-med student at Boston University',
  rating: 5,
  highlight: 'Perfect tracking'
}];


const stats = [
{ value: '47k+', label: 'Loads Completed' },
{ value: '12', label: 'Campuses Served' },
{ value: '98%', label: 'On-Time Delivery' },
{ value: '4.9★', label: 'Average Rating' }];


export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Auto-rotate
  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials?.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('hidden-before-reveal');
          }
        });
      },
      { threshold: 0.1 }
    );

    const els = sectionRef?.current?.querySelectorAll('.reveal, .reveal-scale');
    els?.forEach((el) => {
      el?.classList?.add('hidden-before-reveal');
      observer?.observe(el);
    });

    return () => observer?.disconnect();
  }, []);

  const t = testimonials?.[active];

  return (
    <section id="testimonials" ref={sectionRef} className="py-32 bg-campusbg overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6">
        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          {stats?.map((stat, i) =>
          <div
            key={stat?.label}
            className={`reveal-scale bg-white rounded-xl border border-border p-6 text-center shadow-card`}
            style={{ transitionDelay: `${i * 80}ms` }}>
            
              <p className="text-3xl font-800 text-secondary tracking-tight">{stat?.value}</p>
              <p className="text-muted text-xs font-600 uppercase tracking-wider mt-1">{stat?.label}</p>
            </div>
          )}
        </div>

        {/* Dark testimonial block */}
        <div className="reveal bg-secondary rounded-2xl p-10 md:p-16 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #38BDF8, transparent)' }} />
          <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #1A56DB, transparent)' }} />

          <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Quote */}
            <div className="space-y-8">
              <div className="flex items-center gap-3">
                <span className="badge-pill bg-primary/20 text-accent">Student Stories</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5]?.map((i) =>
                  <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#F59E0B">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  )}
                </div>
              </div>

              <blockquote className="text-white/80 text-xl md:text-2xl font-400 leading-relaxed italic transition-all duration-500">
                &ldquo;{t?.quote}&rdquo;
              </blockquote>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/20 flex-shrink-0">
                  <AppImage
                    src={t?.avatar}
                    alt={t?.altText}
                    width={48} height={48}
                    className="w-full h-full object-cover" />
                  
                </div>
                <div>
                  <p className="text-white font-700 text-base">{t?.name}</p>
                  <p className="text-white/40 text-sm font-400">{t?.role}</p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-3">
                {testimonials?.map((_, i) =>
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  i === active ? 'w-8 bg-primary' : 'w-2 bg-white/20 hover:bg-white/40'}`
                  }
                  aria-label={`View testimonial ${i + 1}`} />

                )}
              </div>
            </div>

            {/* Right: Avatar + highlight */}
            <div className="flex flex-col items-center gap-6">
              <div className="relative">
                <div className="w-48 h-48 rounded-2xl overflow-hidden border-4 border-white/10 shadow-float">
                  <AppImage
                    src={t?.avatar}
                    alt={t?.altText}
                    width={192} height={192}
                    className="w-full h-full object-cover grayscale brightness-110 hover:grayscale-0 transition-all duration-700" />
                  
                </div>
                {/* Highlight pill */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-primary text-white px-5 py-2 rounded-full text-xs font-700 whitespace-nowrap shadow-float">
                  {t?.highlight}
                </div>
              </div>

              {/* All avatars */}
              <div className="flex items-center gap-2 mt-6">
                {testimonials?.map((testimonial, i) =>
                <button
                  key={testimonial?.id}
                  onClick={() => setActive(i)}
                  className={`rounded-full overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                  i === active ?
                  'w-12 h-12 border-primary opacity-100' : 'w-9 h-9 border-white/20 opacity-50 hover:opacity-80'}`
                  }
                  aria-label={`Select ${testimonial?.name}`}>
                  
                    <AppImage
                    src={testimonial?.avatar}
                    alt={testimonial?.altText}
                    width={48} height={48}
                    className="w-full h-full object-cover" />
                  
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}